import React from 'react';
import styled from 'styled-components';
import { Element } from 'react-scroll';

import {
  Alert,
  Anchor,
  Card,
  Flex,
  Heading,
  Paragraph,
  spaceMixin,
  Spacer,
  Text,
  TextButton,
  token,
  Tooltip,
  TooltipPopper,
} from '@zillow/constellation';

import ApplicantNavLinks from './ApplicantNavLinks';
import { getDisplayName } from '../../../../utils/applicantDetailsUtil';
import FormatLogic, { getTitleCase } from '../../../../utils/formatLogicUtil';
import ReauthenticatingAlert from './ReauthenticatingAlert';
import OverviewIdVerifiedTag from './OverviewIdVerifiedTag';
import { useApplicationSummaryContext } from '../../../../contexts/ApplicationSummaryContext';
import { useDispatch, useSelector } from 'react-redux';
import { getNeedsToIdVerifySelector } from '../../../../selectors';
import { getApplicantPath, getIdVerificationPath, getLandlordReviewPath, URLS } from '../../../../constants/urls';
import useQueryVariables from '../../../../hooks/useQueryVariables';
import { useGetIsIdvModuleEnabled } from '../../../../hooks/useGetSplitAb';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { updateIdVerifying } from '../../../../actions/landlordActionCreators';

const StyledCard = styled(Card)`
  :not(:last-child) {
    margin-bottom: ${spaceMixin('lg')};
  }
`;

const ContactItem = styled(Flex)`
  margin-bottom: ${spaceMixin('xs')};
  margin-right: ${spaceMixin('xs')};
`;
ContactItem.displayName = 'ContactItem';

const ApplicantHeaderWrapper = styled('div')`
  padding-bottom: ${spaceMixin('md')};

  :not(:last-child) {
    border-bottom: 1px solid ${token('colors.borderLight')};
  }
`;

const ApplicantStatusLineText = styled(Paragraph)`
  margin-top: ${spaceMixin('sm')};
  font-weight: ${token('fontWeights.bodyHeading')};
  color: ${token('colors.yellow500')};
`;

const Applicant = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const match = useRouteMatch();
  const location = useLocation();
  const { applicants, isApplicationDeclined } = useApplicationSummaryContext();
  const needsToIdVerify = useSelector(getNeedsToIdVerifySelector());
  const { propertyId, applicationId } = useQueryVariables();
  const isIdvModuleEnabled = useGetIsIdvModuleEnabled();

  const getApplicantContact = (applicant) => {
    const {
      userId: applicantId,
      email,
      phoneNumber,
      zgAuthZone,
      isComplete,
      contactInfo: defaultInfo = {},
    } = applicant;
    const displayEmail = isComplete ? email : defaultInfo.coapplicantEmail;
    const displayPhone = isComplete ? phoneNumber : null;

    const idvLink = isComplete
      ? isIdvModuleEnabled
        ? getIdVerificationPath(propertyId)
        : getApplicantPath({
            propertyId,
            applicationId,
            pageLocation: `${URLS.SCREENING}/${URLS.SCREENING_LANDLORD_JUNCTION}`,
            zgAuthZone,
            applicantId,
          })
      : null;

    const landlordReviewLink = getLandlordReviewPath(propertyId, applicationId);

    function viewContactInfo() {
      dispatch(
        updateIdVerifying({ completedLink: landlordReviewLink, previousLink: `${match.url}${location.search}` }),
      );
      history.replace(idvLink);
    }

    const emailCol = displayEmail ? <Anchor href={`mailto:${displayEmail}`}>{email}</Anchor> : null;
    const phoneCol = phoneNumber ? (
      <Anchor href={`tel:${displayPhone}`}>{FormatLogic.phone(displayPhone)}</Anchor>
    ) : null;

    const formattedEmailCol =
      displayEmail && needsToIdVerify ? (
        <Text className="masked-email">{FormatLogic.maskedEmail(email)}</Text>
      ) : (
        emailCol
      );
    const formattedPhoneCol =
      displayPhone && needsToIdVerify ? (
        <Text className="masked-phone">{FormatLogic.phone(displayPhone, true)}</Text>
      ) : (
        phoneCol
      );

    return (
      <Flex display="flex" justifyContent="space-between" flexWrap="wrap">
        <Flex display="inline-flex" flexWrap="wrap">
          {formattedEmailCol && <ContactItem>{formattedEmailCol}</ContactItem>}
          {formattedPhoneCol && <ContactItem>{formattedPhoneCol}</ContactItem>}
        </Flex>
        {idvLink && (formattedPhoneCol || formattedEmailCol) && needsToIdVerify && (
          <Flex display="flex" gap="xxs" alignItems="center">
            <TooltipPopper
              className="view-contact-info-idv-tooltip"
              triggered={
                <Tooltip
                  body={
                    <Paragraph>
                      To view this sensitive information and protect applicants’ personal information, you’ll need to
                      verify your identity with Experian.
                    </Paragraph>
                  }
                />
              }
            />
            <TextButton className="view-contact-info-idv" onClick={viewContactInfo}>
              View contact info
            </TextButton>
          </Flex>
        )}
      </Flex>
    );
  };

  const getApplicantStatusLine = ({ isNotStarted, isComplete, firstName, contactInfo: defaultInfo = {} }) => {
    const { coapplicantFirstName: defaultFirstName } = defaultInfo;
    const displayFirstName = firstName || defaultFirstName;

    if (isNotStarted) {
      return (
        <>
          <ApplicantStatusLineText>Application not started</ApplicantStatusLineText>
          <Paragraph>
            {displayFirstName} has not started their application. Once they apply, we'll notify you by email.
          </Paragraph>
        </>
      );
    }

    if (!isComplete) {
      return (
        <>
          <ApplicantStatusLineText>Application started, not submitted</ApplicantStatusLineText>
          <Paragraph>
            {displayFirstName} has not yet submitted their application. Once they apply, we'll notify you by email.
          </Paragraph>
        </>
      );
    }
  };

  const getApplicantHeader = (applicant) => {
    const { firstName, middleName, lastName, isComplete, contactInfo: defaultInfo = {} } = applicant;
    const { coapplicantFirstName: defaultFirstName, coapplicantLastName: defaultLastName } = defaultInfo;
    const name =
      getDisplayName(firstName, middleName, lastName) || getDisplayName(defaultFirstName, null, defaultLastName);

    return (
      <ApplicantHeaderWrapper>
        <Spacer marginBottom="sm">
          <Flex display="flex" gap="xs" flexWrap="wrap">
            <Heading level={4} as="h2">
              {getTitleCase(name)}
            </Heading>
            {isComplete && <OverviewIdVerifiedTag applicant={applicant} />}
          </Flex>
        </Spacer>
        {getApplicantContact(applicant)}
        {getApplicantStatusLine(applicant)}
      </ApplicantHeaderWrapper>
    );
  };

  return (
    <div>
      {applicants.map((applicant) => {
        return (
          <Element
            key={applicant.userId || applicant?.contactInfo?.coapplicantEmail}
            name={applicant.userId || applicant?.contactInfo?.coapplicantEmail}
            className="stacked-lg"
          >
            <StyledCard cardType="secondary" padding="sm" mediaQuery={{ lg: { padding: 'lg' } }}>
              {isApplicationDeclined && (
                <Spacer marginBottom="md">
                  <Alert
                    id="applicant__declined-alert"
                    body="To protect the applicant's privacy, you can no longer view their application details and consumer reports."
                  />
                </Spacer>
              )}
              <ReauthenticatingAlert {...{ applicant }} />
              {getApplicantHeader(applicant)}
              {applicant.isComplete && <ApplicantNavLinks applicant={applicant} />}
            </StyledCard>
          </Element>
        );
      })}
    </div>
  );
};

export default Applicant;
